import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import { Input, Modal, Subheading } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import RecipientSelector from 'packages/kakapo-ui-kit/components/recipients/RecipientSelector';
import { Recipient } from 'packages/kakapo-ui-services/models/Recipient';

import FormErrorMessage from '../../../../kakapo-ui-kit/components/forms/errors/FormErrorMessage';

export default function FeedbackRequestModalRecipients(props: {
	returnURL: string;
	error?: any;
	recipients: Recipient[];
	onBack: () => void;
	onChange: (recipients: Recipient[]) => void;
	onDone: (dueDate: Date, internals: string[], externals: graphql.FeedbackExternal[]) => void;
	feedbackTemplateID?: string;
	minimumRecipients?: number;
	maximumRecipients?: number;
}) {
	const { t } = useTranslation();
	
	const [error, setError] = useState<Error | undefined>(undefined);
	const nextWeek = new Date(Date.now() + 7 * 24 * 3600 * 1000);
	const [dueDate, setDueDate] = useState(nextWeek);

	const internals = props.recipients
		.filter((recipient) => 'userId' in recipient)
		.map((recipient) => (recipient as any).userId);

	const externals = props.recipients
		.filter((recipient) => 'name' in recipient)
		.map(
			(recipient) =>
				({
					firstName: (recipient as any).name.split(' ')[0],
					lastName: (recipient as any).name.split(' ').slice(1).join(' '),
					email: (recipient as any).email,
				} as graphql.FeedbackExternal),
		);

	return (
		<Modal>
			<Modal.Header title={t('common.feedback.selectRecipients')} />

			<Modal.Contents>
				{props.error && <GraphQLErrorMessage error={props.error} />}
				{error && <FormErrorMessage error={error} />}
				<RecipientSelector
					initialRecipients={props.recipients}
					onChange={props.onChange}
					footer={
						<>
							<Subheading mb={1}>{t('dueDate.selector.label')}</Subheading>
							<Flex>
								<Input.DatePicker
									value={dueDate!}
									onChange={(dueDate) => setDueDate(dueDate ?? nextWeek)}
								/>
							</Flex>
						</>
					}
				/>
			</Modal.Contents>
			<Modal.FormFooter
				onCancel={() => props.onBack()}
				disabled={dueDate <= new Date()}
				saveTitle={t('common.feedback.nextStepSend')}
				onSave={() => {
					if (props.recipients.length < (props.minimumRecipients || 1)) {
						setError(
							new Error(
								t('common.modules.feedback.minimumGivers', {
									count: props.minimumRecipients || 1,
								}),
							),
						);
						return;
					}

					if (!!props.maximumRecipients && props.recipients.length > props.maximumRecipients) {
						setError(
							new Error(
								t('common.modules.feedback.maximumGivers', {
									count: props.maximumRecipients,
								}),
							),
						);
						return;
					}

					props.onDone(dueDate, internals, externals);
				}}
			/>
		</Modal>
	);
}
